import { Checkbox } from '@monorepo/shared/componentsV2/Checkbox';
import { getFieldNameWithPrefix } from '@monorepo/shared/componentsV2/fields/fieldUtils';
import { MultiSelectField } from '@monorepo/shared/componentsV2/fields/MultiSelectField';
import { formLocationFieldsProductGuideUrl } from '@monorepo/shared/externalLinks/productGuide';
import { hasAtLeastOneValue } from '@monorepo/shared/utils/validators';
import { FormApi } from 'final-form';
import {
  FormTemplateFieldTypes,
  getFormFieldDefaultSubtext,
  mapLayerTagLabels,
  MapLayerTags,
} from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import { useField, useForm } from 'react-final-form';
import { ModalFieldWrapper } from '../styled';
import {
  BaseEditGenericFieldFormProps,
  EditGenericFieldForm,
} from './GenericFieldForm/EditGenericFieldForm';

const MapLayerTagsSet = new Set(Object.values(MapLayerTags));

const layerTagOptions = Object.values(MapLayerTags)
  .map((value) => ({
    value,
    label: mapLayerTagLabels[value],
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

/**
 * If the user hadn't changed the subtext, overwrite it with the new field type's default
 * subtext when changing the location field type. Otherwise, keep the user input.
 *
 * If we start switching field types for other fields (e.g. select fields), this might
 * move somewhere more generic.
 */
function getNewSubtextIfUntouched({
  form,
  newFieldType,
  typeFieldName,
  subtextFieldName,
}: {
  form: FormApi;
  newFieldType: FormTemplateFieldTypes;
  typeFieldName: string;
  subtextFieldName: string;
}): string {
  const [oldFieldType, oldSubtext] = [
    form.getFieldState(typeFieldName)?.value,
    form.getFieldState(subtextFieldName)?.value,
  ];
  const isSubtextUntouched =
    oldSubtext === getFormFieldDefaultSubtext(oldFieldType);
  return isSubtextUntouched
    ? getFormFieldDefaultSubtext(newFieldType)
    : oldSubtext;
}

function useSwitchBetweenSingleAndMultiLocation(
  typeFieldName: string,
  subtextFieldName: string,
) {
  const form = useForm();

  return useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isBecomingMultiLocationField = event.target.checked;
      const newFieldType = isBecomingMultiLocationField
        ? FormTemplateFieldTypes.MULTI_LOCATION
        : FormTemplateFieldTypes.SINGLE_LOCATION;

      const newSubtext = getNewSubtextIfUntouched({
        form,
        newFieldType,
        typeFieldName,
        subtextFieldName,
      });

      form.batch(() => {
        form.change(typeFieldName, newFieldType);
        form.change(subtextFieldName, newSubtext);
      });
    },
    [form, typeFieldName, subtextFieldName],
  );
}

function useIsMultiLocationField(typeFieldName: string) {
  const {
    input: { value: fieldType },
  } = useField<FormTemplateFieldTypes>(typeFieldName, {
    subscription: { value: true },
  });

  return fieldType === FormTemplateFieldTypes.MULTI_LOCATION;
}

/**
 * displaying invalid options (added likely via the admin panel) so that the user can unselect them
 * and fix the invalid form so it'll save
 */
function useOptionsIncludingInvalidOnes(layerTagsFieldName: string) {
  const {
    input: { value: layerTagsValue },
  } = useField<MapLayerTags[]>(layerTagsFieldName, {
    subscription: { value: true },
  });

  return useMemo(() => {
    const invalidSelectedTags = (layerTagsValue || []).filter(
      (tag) => !MapLayerTagsSet.has(tag),
    );
    const invalidOptions = invalidSelectedTags.map((tag) => ({
      value: tag,
      label: tag,
      isInvalid: true,
    }));

    return [...invalidOptions, ...layerTagOptions];
  }, [layerTagsValue]);
}

type EditLocationFieldFormProps = BaseEditGenericFieldFormProps;

export function EditLocationFieldForm(props: EditLocationFieldFormProps) {
  const { isEditMode, prefix } = props;

  const [typeFieldName, layerTagsFieldName, subtextFieldName] = [
    getFieldNameWithPrefix('type', prefix),
    getFieldNameWithPrefix('locationLayerTags', prefix),
    getFieldNameWithPrefix('nameSubtext', prefix),
  ];

  const handleLocationFieldTypeChange = useSwitchBetweenSingleAndMultiLocation(
    typeFieldName,
    subtextFieldName,
  );

  const optionsIncludingInvalidOnes =
    useOptionsIncludingInvalidOnes(layerTagsFieldName);

  const isMultiLocationField = useIsMultiLocationField(typeFieldName);
  const layerTagsLabel = isMultiLocationField
    ? 'Allow users to choose locations from layers with these tags'
    : 'Allow users to choose a location from layers with these tags';

  return (
    <EditGenericFieldForm {...props}>
      <ModalFieldWrapper>
        <Checkbox
          name={getFieldNameWithPrefix('isMultiLocation', prefix)}
          label="Allow selecting multiple locations in the form submission"
          checked={isMultiLocationField}
          onChange={handleLocationFieldTypeChange}
          disabled={isEditMode}
          focusableWhenDisabled
          tooltip={
            isEditMode ? 'Cannot be changed after the field is created.' : ''
          }
        />
      </ModalFieldWrapper>
      <ModalFieldWrapper>
        <MultiSelectField<MapLayerTags>
          name={layerTagsFieldName}
          label={layerTagsLabel}
          options={optionsIncludingInvalidOnes}
          required
          explanationUrl={formLocationFieldsProductGuideUrl}
          validate={hasAtLeastOneValue}
        />
      </ModalFieldWrapper>
    </EditGenericFieldForm>
  );
}
